import React, { useEffect, useRef, useState } from 'react'
import { observer } from "mobx-react";
import { useStores } from "../../stores/rootStore";
import {Container, TableCell, Tooltip, TableRow, Typography} from '@material-ui/core';
import {ActionDto, ManagerDto, ReviewEmployeeAccountDto, ReviewEmployeeDto} from 'src/stores/models/qarFormModels';
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded';
import DynamicFeedIcon from '@material-ui/icons/DynamicFeed';
import { QARFormDelegate } from './delegate';
import { QARFormAccounts } from './accounts';
import { Skeleton } from '@material-ui/lab';
import MaterialTable, {MTableToolbar} from 'material-table';
import QARFormHelper from './helper';
import GroupAddSharpIcon from '@material-ui/icons/GroupAddSharp';
import Grid from "@material-ui/core/Grid";
import {QARFormBulkReview} from "./bulkReview";
import {QARFormActionSelect} from "./action";
import {QARFormAccountBulkReview} from "./accountBulkReview";
import {QARFormCommentInput} from "./comment";
import SettingsApplicationsIcon from "@material-ui/icons/SettingsApplications";

export const QARFormTable = observer(() => {

  const {qarFormStore} = useStores();

  const tableRef = useRef<any>(undefined);

  const defaultTableOptions = {
    filtering: false,
    grouping: false,
    draggable: false,
    paging: true,
    search: true,
    toolbar: true,
    showTitle: false,
    sorting: true
  };

  const [filterEmployeesTable, toggleFilterEmployeesTable] = useState(false);
  const [filterUnmatchedAccountsTable, toggleFilterUnmatchedAccountsTable] = useState(false);
  const [shouldExpand, setShouldExpand] = useState(true);

  useEffect(() => {
    if (!tableRef.current) {
      return;
    }
    if (!isRowExpanded(0) && shouldExpand) {
      expandRow(0);
      setShouldExpand(false)
    }
  })

  useEffect(() => {

    if (!tableRef) {
      return;
    }

    if (qarFormStore.walkthrough) {
      if (!isRowExpanded(0)) {
        expandRow(0);
      }
    }

    if (!qarFormStore.employeesWithError?.length) {
      return;
    }

    const errorRowIndexes = qarFormStore.employeesWithError.map((employee: ReviewEmployeeDto) => {
      return getEmployeeIndex(employee.email);
    })

    errorRowIndexes.forEach((errorRowIndex: number) => {
      if (errorRowIndex < 0) {
        return;
      }
      if (!isRowExpanded(errorRowIndex)) {
        expandRow(errorRowIndex);
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qarFormStore.employeesWithError, qarFormStore.walkthrough])

  const getEmployeeIndex = (employeeEmail: string): number => {
    return tableRef.current.state.renderData.findIndex((employee: ReviewEmployeeDto) => {
      return employee.email === employeeEmail;
    })
  }

  const isRowExpanded = (rowIndex: number): boolean => {
    return tableRef.current.dataManager.sortedData[rowIndex].tableData.showDetailPanel !== undefined;
  }

  const expandRow = (rowIndex: number): void => {
    tableRef.current.onToggleDetailPanel([rowIndex], renderAccountsPanel);
  }

  const renderAccountsPanel = (data: ReviewEmployeeDto) => {

    const employeesAccounts = qarFormStore.employees!.find(x => x.id === data.id)?.accounts;
    const employeesAccountsChanges = qarFormStore.employeesChanges!.find(x => x.id === data.id)?.accounts;

    return (
      <div className='qar-form-employee-accounts-wrapper'>
        {/* <QARFormDelegate accounts={employeesAccounts!} accountsChanges={employeesAccountsChanges!} /> */}
        <QARFormAccounts accounts={employeesAccounts!} accountsChanges={employeesAccountsChanges!} />
      </div>
    );
  }

  const getPageSizeOptions = (allRecordsCount: number, defaultPageSizeOptions: number[]) => {

    let largestPageSizeOption = Math.max(...defaultPageSizeOptions);
    //show option for all records if it's larger than default one
    if(allRecordsCount>largestPageSizeOption){
      defaultPageSizeOptions.push(allRecordsCount);
    }

    return defaultPageSizeOptions;
  };

  const renderIndicators = (employee: ReviewEmployeeDto) => {

    if (qarFormStore.employees === undefined || qarFormStore.employeesChanges === undefined ||
      qarFormStore.delegatedUnmatchedAccounts === undefined || qarFormStore.delegatedUnmatchedAccountsChanges === undefined) {
      return <></>
    }

    const delegatedFromManager = QARFormHelper.employeeDelegatedFrom(employee);
    const employeesAccounts = employee?.accounts;
    const employeesAccountsChanges = qarFormStore.employeesChanges!.find(x => x.id === employee.id)?.accounts;
    const isUnmatchedAccount = qarFormStore.delegatedUnmatchedAccounts?.find(x => x.id === employee.id);

    if (delegatedFromManager) {
      if(isUnmatchedAccount){
        return(
          <Tooltip
            title={QARFormHelper.renderTooltipMessage(`This account has been delegated to you from ${delegatedFromManager.fullNames}`)} placement="top" arrow>
            <GroupAddSharpIcon fontSize="large"></GroupAddSharpIcon>
          </Tooltip>
        )
      }

      return (
        <QARFormDelegate employee={employee!} accounts={employeesAccounts!} accountsChanges={employeesAccountsChanges!} />
      );
    }

    const delegatedTo = QARFormHelper.employeeDelegatedTo(employee);

    if (delegatedTo) {
      return (
        <Tooltip title={QARFormHelper.renderTooltipMessage(`You have delegated this employee review to ${(delegatedTo as ManagerDto).fullName}`)} placement='top' arrow>
          <DynamicFeedIcon fontSize="large"></DynamicFeedIcon>
        </Tooltip>
      );
    }

    const isEmployeeReviewCompleted = QARFormHelper.isEmployeeReviewCompleted(employee);

    if (isEmployeeReviewCompleted) {
      return (
        <Tooltip title={QARFormHelper.renderTooltipMessage(`You have completed the review for this employee`)} placement='top' arrow>
          <CheckCircleOutlineRoundedIcon fontSize="large" htmlColor='#2DBF64'></CheckCircleOutlineRoundedIcon>
        </Tooltip>
      );
    }

    if(isUnmatchedAccount){
      return(<></>)
    }

    return <QARFormDelegate employee={employee!} accounts={employeesAccounts!} accountsChanges={employeesAccountsChanges!} />
  }

  const renderCell = (r: any) => {

    if (r.columnDef.field === 'indicators') {
      return (
        <TableCell align="center" width="12%">
          {renderIndicators(r.rowData as ReviewEmployeeDto)}
        </TableCell>
      );
    }

    if (r.columnDef.field === 'unmatchedUsername') {
      return <TableCell align="left">{r.rowData.accounts[0].username}</TableCell>
    }

    // default
    return (
      <TableCell align="left">{r.value}</TableCell>
    );

  }

  const renderTable = () => {
    if (qarFormStore.employees === undefined || qarFormStore.employeesChanges === undefined || qarFormStore.allManagers === undefined) {
      return (
        <Container>
          <Skeleton variant="text" width="60%" style={{ margin: "15px 0", display: "inline-block" }} />
          <Skeleton variant="text" width="60%" style={{ margin: "15px 0", display: "inline-block" }} />
          <Skeleton variant="text" width="60%" style={{ margin: "15px 0", display: "inline-block" }} />
          <Skeleton variant="text" width="60%" style={{ margin: "15px 0", display: "inline-block" }} />
        </Container>
      );
    }

    if (qarFormStore.employees.length === 0) {
      return (<><p>No employees found for review.</p></>);
    }

    return (
      <span className="allDirectReports">
        <MaterialTable
          options={{
            ...defaultTableOptions, pageSize: 10, pageSizeOptions: getPageSizeOptions(qarFormStore.employees.length, [5, 10, 20]),
            rowStyle: (rowData: ReviewEmployeeDto) => ({
              backgroundColor: (!QARFormHelper.employeeDelegatedTo(rowData)) ? '#FFF' : '#EEE',
            }),
            searchFieldAlignment: "left",
            hideFilterIcons: true,
            filtering: filterEmployeesTable
          }}
          actions={[{
            icon: "filter_list",
            onClick: () => toggleFilterEmployeesTable(currentFilter => !currentFilter),
            position: 'toolbar'
          }]}
          tableRef={tableRef}
          data={qarFormStore.employees}
          columns={[
            { title: 'Name', field: 'fullNames', width: "35%", defaultSort: "asc" },
            { title: 'Title', field: 'title', width: "30%" },
            { title: 'Email', field: 'email', width: "30%" },
            { title: '', field: 'indicators', width: "5%", sorting: false }
          ]}
          components={{
            Cell: renderCell,
            Toolbar: (props) => (
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Grid item sm={4} style={{ textAlign: 'right' }}>
                  <QARFormBulkReview isSystemAccounts={false}></QARFormBulkReview>
                </Grid>
                <Grid item sm={8} style={{ display: 'flex', justifyContent: 'right' }}>
                  <MTableToolbar {...props} />
                </Grid>
              </Grid>
            )
          }}
          detailPanel={[
            {
              tooltip: 'Expand',
              render: renderAccountsPanel
            }
          ]}
        />
      </span>
    );
  }

  const renderUnmatchedAccounts = () => {

    if (qarFormStore.delegatedUnmatchedAccounts?.length === 0) {
      return <></>
    }

    if (qarFormStore.delegatedUnmatchedAccounts === undefined || qarFormStore.delegatedUnmatchedAccountsChanges === undefined || qarFormStore.allManagers === undefined) {
      return (
        <Container>
                    <Skeleton variant="text" width="60%" style={{margin: '15px 0', display: 'inline-block'}}/>
                    <Skeleton variant="text" width="60%" style={{margin: '15px 0', display: 'inline-block'}}/>
                    <Skeleton variant="text" width="60%" style={{margin: '15px 0', display: 'inline-block'}}/>
                    <Skeleton variant="text" width="60%" style={{margin: '15px 0', display: 'inline-block'}}/>
        </Container>
      );
    }

    const renderTooltipMessage = (tooltipMessage: string) => {
      return <Typography component="h2" align='center'>{tooltipMessage}</Typography>
    }

    const renderEmployeeChameleonAdministratorIndicator = (employeeAccount: ReviewEmployeeAccountDto) => {
      if (employeeAccount.isChameleonAdmin) {
        return (
            <Tooltip title={renderTooltipMessage("The user is a Chameleon administrator in this account")} placement='top' arrow>
              <SettingsApplicationsIcon fontSize="large"></SettingsApplicationsIcon>
            </Tooltip>
        );
      }
    }

    const renderAuxiliaryRow = (employeeAccount: ReviewEmployeeDto, action: ActionDto, actionChanges: ActionDto) => {
      return <QARFormCommentInput employeeAccount={employeeAccount.accounts[0]} action={action} actionChanges={actionChanges}></QARFormCommentInput>;
    }

    const renderSystemAcountRow = (systemAccount: ReviewEmployeeDto, action: ActionDto, actionChanges: ActionDto) => {

      return (
          <>
            <TableRow>
              <TableCell className='accountActions'>
                <QARFormAccountBulkReview action={action} actionChanges={actionChanges}></QARFormAccountBulkReview>
              </TableCell>
              <TableCell>
                {systemAccount.fullNames}
              </TableCell>
              <TableCell>
                {QARFormHelper.renderPermissionGroups(systemAccount.accounts[0])}
              </TableCell>
              <TableCell>
                {systemAccount.accounts[0].systemName}
              </TableCell>
              <TableCell>
                {systemAccount.accounts[0].displayName}
              </TableCell>
              <TableCell>
                {systemAccount.title}
              </TableCell>
              <TableCell align="left">
                {QARFormHelper.renderRole(systemAccount.accounts[0].roles)}
              </TableCell>
              <TableCell>
                {systemAccount.email}
              </TableCell>
              <TableCell className='accountActions'>
                <QARFormActionSelect action={action} actionChanges={actionChanges}></QARFormActionSelect>
              </TableCell>
              <TableCell align="center">
                {renderEmployeeChameleonAdministratorIndicator(systemAccount.accounts[0])}
              </TableCell>
            </TableRow>
          </>
      );
    }
    return (
      <>
        <span className="systemAccounts">
          <h3>System accounts assigned to you</h3>
          <p style={{ fontStyle: 'italic', fontSize: 'medium', fontWeight: 'bold' }}>
            If you don't recognize or you are not responsible for any of the listed accounts,
            please inform technicalcompliance@draftkings.com
          </p>
          <MaterialTable
            options={{
              ...defaultTableOptions,
              pageSizeOptions: getPageSizeOptions(qarFormStore.delegatedUnmatchedAccounts.length, [5, 10, 20]),
              rowStyle: (rowData: ReviewEmployeeDto) => ({
                backgroundColor: (!QARFormHelper.employeeDelegatedTo(rowData)) ? '#FFF' : '#EEE',
              }),
              searchFieldAlignment: "left",
              hideFilterIcons: true,
              filtering: filterUnmatchedAccountsTable
            }}
            actions={[{
              icon: "filter_list",
              onClick: () => toggleFilterUnmatchedAccountsTable(currentFilter => !currentFilter),
              position: 'toolbar'
            }]}
            data={qarFormStore.delegatedUnmatchedAccounts}
            columns={[
              {
                title: '',
                field: 'BulkReview',
                width: "2%",
                sorting: false
              },
              {
                title: 'Name',
                field: 'fullNames',
                width: "15%",
                defaultSort: "asc"
              },
              {
                title: 'Username',
                field: 'unmatchedUsername',
                type: 'string',
                width: "15%",
                customSort: (a, b) => a.accounts[0].username.localeCompare(b.accounts[0].username),
                customFilterAndSearch: (term, rowData) => {
                  const filter = term.toLowerCase();
                  const usernames = rowData.accounts.map(x => x.username).join().toLowerCase();
                  return usernames.indexOf(filter) !== -1;
                }
              },
              {
                title: 'System',
                field: 'systemName',
                customSort: (a, b) => a.accounts[0].systemName.localeCompare(b.accounts[0].systemName),
                width: "10%"
              },
              {
                title: 'Account',
                field: 'displayName',
                customSort: (a, b) => a.accounts[0].displayName.localeCompare(b.accounts[0].displayName),
                width: "7%"
              },
              {
                title: 'Title',
                field: 'title',
                width: "11%"
              },
              {
                title: 'Role', field: 'role',
                width: "15%",
                sorting: false
              },
              {
                title: 'Email',
                field: 'email',
                width: "15%"
              },
              {
                title: 'Actions',
                field: 'actions',               
                width: "7%",
                sorting: false
              },
              {
                title: '',
                field: 'indicators',              
                width: "3%",
                sorting: false
              }
            ]}
            components={{
              Row: ({ data }: { data: ReviewEmployeeDto }) => {

                const systemAccount = data;
                const action = qarFormStore.delegatedUnmatchedAccounts!.find(x => x.id === systemAccount.id)!.accounts[0].action;
                const actionChanges = qarFormStore.delegatedUnmatchedAccountsChanges!.find(x => x.id === systemAccount.id)!.accounts[0].action;

                return (
                    <>
                      {renderSystemAcountRow(systemAccount, action, actionChanges)}
                      {renderAuxiliaryRow(systemAccount, action, actionChanges)}
                    </>
                )
              },
              Toolbar: (props) => (
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                          <Grid item sm={4} style={{textAlign: 'right'}}>
                    <QARFormBulkReview isSystemAccounts={true}></QARFormBulkReview>
                  </Grid>
                          <Grid item sm={8} style={{display: 'flex', justifyContent: 'right'}}>
                    <MTableToolbar {...props} />
                  </Grid>
                </Grid>
              )
            }}
          />
        </span>
      </>
    );
  }

  return (
    <>
      {renderTable()}
      {renderUnmatchedAccounts()}
    </>
  );
});